import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/Navigation';
import CosmicBackground from '../../components/CosmicBackground';
import ToolInterface from '../../components/ToolInterface';
import { getToolById } from '../../data/toolsData';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiInstagram, FiTwitter, FiLinkedin, FiFacebook } = FiIcons;

const SocialMediaCreatorPage = () => {
  const tool = getToolById('social-media-creator');
  const [platform, setPlatform] = useState('instagram');
  const [content, setContent] = useState('');
  const [tone, setTone] = useState('casual');
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState('');

  const platforms = [
    { id: 'instagram', name: 'Instagram', icon: FiInstagram, limit: 2200 },
    { id: 'twitter', name: 'Twitter/X', icon: FiTwitter, limit: 280 },
    { id: 'linkedin', name: 'LinkedIn', icon: FiLinkedin, limit: 3000 },
    { id: 'facebook', name: 'Facebook', icon: FiFacebook, limit: 63206 }
  ];

  const handleGenerate = async () => {
    if (!content.trim()) return;
    
    setIsGenerating(true);
    
    setTimeout(() => {
      const selectedPlatform = platforms.find(p => p.id === platform);
      let generatedPost = '';

      if (platform === 'twitter') {
        generatedPost = `🚀 ${content}

${tone === 'professional' ? 'Key insights:' : 'Here\'s the thing:'}
✨ Innovation drives success
💡 Consistency beats perfection
🎯 Focus on your audience

${includeHashtags ? '#entrepreneur #success #motivation #growth' : ''}`;
      } else if (platform === 'linkedin') {
        generatedPost = `${content}

In my experience working with entrepreneurs and business leaders, I've observed that success often comes down to three fundamental principles:

🔹 Clarity of vision - Know exactly where you're headed
🔹 Consistent execution - Small daily actions compound over time  
🔹 Continuous learning - Adapt and evolve with the market

What's been your biggest game-changer in business?

${includeHashtags ? '#Leadership #Entrepreneurship #BusinessGrowth #Innovation #Success' : ''}`;
      } else if (platform === 'instagram') {
        generatedPost = `${content} ✨

${tone === 'casual' ? '💭 Real talk:' : '🎯 Key takeaway:'}
Success isn't about perfection, it's about progress. Every small step forward counts, and every lesson learned makes you stronger.

Remember: Your journey is unique, and that's your superpower! 🌟

What's one thing you're working on today? Drop it in the comments! 👇

${includeHashtags ? '#entrepreneur #motivation #success #mindset #growth #hustle #businessowner #inspiration #goals #progress' : ''}`;
      } else {
        generatedPost = `${content}

I wanted to share some thoughts on this topic because it's something that's been on my mind lately. In our fast-paced world, it's easy to get caught up in the hustle and forget about the fundamentals that really matter.

Here are a few things I've learned:
• Authenticity always wins in the long run
• Building genuine relationships is more valuable than any marketing strategy
• Consistency beats intensity every single time
• Your mindset determines your success more than your circumstances

I'd love to hear your thoughts on this. What's been your experience?

${includeHashtags ? '#business #entrepreneur #success #motivation #growth' : ''}`;
      }

      setResult(generatedPost);
      setIsGenerating(false);
    }, 1500);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <CosmicBackground />
      <Navigation />
      
      <section className="pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <ToolInterface
              tool={tool}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              result={result}
              onCopy={handleCopy}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Platform
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {platforms.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setPlatform(p.id)}
                        className={`flex items-center space-x-2 p-3 rounded-lg border transition-all duration-300 ${
                          platform === p.id
                            ? 'bg-green-600/20 border-green-500 text-green-300'
                            : 'bg-gray-700/50 border-gray-600/50 text-gray-300 hover:bg-gray-600/50'
                        }`}
                      >
                        <SafeIcon icon={p.icon} />
                        <span className="font-poppins text-sm">{p.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Content Topic *
                  </label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="e.g., Starting a business while working full-time"
                    rows={3}
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Tone
                  </label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  >
                    <option value="casual">Casual & Friendly</option>
                    <option value="professional">Professional</option>
                    <option value="inspiring">Inspiring & Motivational</option>
                    <option value="educational">Educational</option>
                  </select>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="hashtags"
                    checked={includeHashtags}
                    onChange={(e) => setIncludeHashtags(e.target.checked)}
                    className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                  />
                  <label htmlFor="hashtags" className="text-sm font-poppins text-gray-300">
                    Include relevant hashtags
                  </label>
                </div>
              </div>
            </ToolInterface>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SocialMediaCreatorPage;